import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const Section = ({ title, children }) => (
  <div className="card">
    <h3 style={{ fontSize: 14, marginBottom: 10 }}>{title}</h3>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div className="form-group" style={{ marginBottom: 10 }}>
    <label className="form-label">{label}</label>
    {children}
  </div>
);

const BreedingRecordPage = () => {
  const { id, recordId } = useParams();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [obsNote, setObsNote] = useState('');
  const [obsDate, setObsDate] = useState(new Date().toISOString().slice(0, 10));

  const load = async () => {
    try {
      const { data } = await api.get(`/breeding/record/${recordId}`);
      setRec(data);
    } catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [recordId]);

  const set = (field) => (e) => { const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value; setRec(p => ({ ...p, [field]: v })); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/breeding/record/${recordId}`, rec);
      setRec(prev => ({ ...prev, ...data }));
      toast.success('Saved');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  const toggleMilestone = async (m) => {
    try { await api.put(`/breeding/milestones/${m.id}`, { is_done: !m.is_done }); load(); }
    catch { toast.error('Failed'); }
  };
  const deleteMilestone = async (mid) => {
    try { await api.delete(`/breeding/milestones/${mid}`); load(); } catch { toast.error('Failed'); }
  };
  const addObservation = async () => {
    if (!obsNote.trim()) return;
    try {
      await api.post(`/breeding/record/${recordId}/observations`, { observed_date: obsDate, note: obsNote });
      setObsNote('');
      load();
    } catch { toast.error('Failed'); }
  };
  const deleteObservation = async (oid) => {
    try { await api.delete(`/breeding/observations/${oid}`); load(); } catch { toast.error('Failed'); }
  };

  if (loading || !rec) return <div className="loading-screen"><div className="spinner" /></div>;

  const costFields = [
    ['stud_fee', 'Stud fee'], ['collection_fee', 'Collection fee'], ['insemination_fee', 'Insemination fee'],
    ['shipping_fee', 'Shipping'], ['vet_fee', 'Vet fee'], ['other_fee', 'Other'],
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', paddingBottom: 80 }}>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate(`/horses/${id}/breeding`)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 20, padding: '4px 8px 4px 0' }}>‹</button>
          <h1 style={{ color: 'white', fontSize: 17, flex: 1 }}>{rec.season_year || 'Breeding'} · {rec.stallion_name || 'Record'}</h1>
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {/* OVERVIEW */}
        <Section title="Overview">
          <Field label="Status">
            <select className="form-input" value={rec.status} onChange={set('status')}>
              <option value="planned">Planned</option>
              <option value="bred">Bred</option>
              <option value="confirmed_in_foal">Confirmed in foal</option>
              <option value="not_in_foal">Not in foal</option>
              <option value="foaled">Foaled</option>
              <option value="lost">Lost</option>
            </select>
          </Field>
          <Field label="Season year"><input className="form-input" type="number" value={rec.season_year || ''} onChange={set('season_year')} /></Field>
          <Field label="Method">
            <select className="form-input" value={rec.method} onChange={set('method')}>
              <option value="live_cover">Live cover</option>
              <option value="fresh_ai">Fresh / cooled AI</option>
              <option value="frozen_ai">Frozen AI</option>
            </select>
          </Field>
          {rec.estimated_foaling_start && (
            <div style={{ fontSize: 13, color: 'var(--cr-primary)', fontWeight: 600, marginTop: 4 }}>
              Estimated foaling: {rec.estimated_foaling_start} – {rec.estimated_foaling_end}
            </div>
          )}
        </Section>

        {/* STALLION */}
        <Section title="Stallion">
          <Field label="Stallion name"><input className="form-input" value={rec.stallion_name || ''} onChange={set('stallion_name')} /></Field>
          <Field label="Stallion owner"><input className="form-input" value={rec.stallion_owner || ''} onChange={set('stallion_owner')} /></Field>
        </Section>

        {/* BREEDING & CYCLE */}
        <Section title="Breeding & cycle">
          <Field label="Heat start date"><input className="form-input" type="date" value={rec.heat_start_date || ''} onChange={set('heat_start_date')} /></Field>
          <Field label="Lutalyse date"><input className="form-input" type="date" value={rec.lutalyse_date || ''} onChange={set('lutalyse_date')} /></Field>
          <Field label="Ovulation induction date"><input className="form-input" type="date" value={rec.ovulation_induction_date || ''} onChange={set('ovulation_induction_date')} /></Field>
          <Field label="Breeding / insemination date"><input className="form-input" type="date" value={rec.breeding_date || ''} onChange={set('breeding_date')} /></Field>
          <Field label="Ovulation date"><input className="form-input" type="date" value={rec.ovulation_date || ''} onChange={set('ovulation_date')} /></Field>
        </Section>

        {/* PREGNANCY */}
        <Section title="Pregnancy">
          <Field label="Confirmed in-foal date"><input className="form-input" type="date" value={rec.confirmed_date || ''} onChange={set('confirmed_date')} /></Field>
        </Section>

        {/* FOALING */}
        <Section title="Foaling">
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, marginBottom: 10 }}>
            <input type="checkbox" checked={!!rec.has_caslick} onChange={set('has_caslick')} style={{ accentColor: 'var(--cr-primary)' }} />
            Has a Caslick (must be opened before foaling)
          </label>
          {rec.has_caslick && <Field label="Caslick opened date"><input className="form-input" type="date" value={rec.caslick_opened_date || ''} onChange={set('caslick_opened_date')} /></Field>}
          <Field label="Foal monitor / device"><input className="form-input" value={rec.foal_monitor_device || ''} onChange={set('foal_monitor_device')} placeholder="e.g. Foalert" /></Field>
          <Field label="Domperidone start date"><input className="form-input" type="date" value={rec.domperidone_start_date || ''} onChange={set('domperidone_start_date')} /></Field>
          <Field label="Actual foaling date"><input className="form-input" type="date" value={rec.actual_foaling_date || ''} onChange={set('actual_foaling_date')} /></Field>
        </Section>

        {/* COSTS */}
        <Section title="Costs">
          {costFields.map(([f, label]) => (
            <Field key={f} label={label}>
              <input className="form-input" type="number" step="0.01" value={rec[f] || ''} onChange={set(f)} placeholder="0.00" />
            </Field>
          ))}
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--cr-primary)', marginTop: 4 }}>
            Total: ${(rec.total_cost || 0).toFixed ? rec.total_cost.toFixed(2) : rec.total_cost}
          </div>
        </Section>

        {/* NOTES */}
        <Section title="Notes">
          <textarea className="form-input" rows={3} value={rec.notes || ''} onChange={set('notes')} />
        </Section>

        <button className="btn btn-primary btn-full btn-lg" onClick={save} disabled={saving} style={{ marginBottom: 16 }}>
          {saving ? 'Saving...' : 'Save record'}
        </button>

        {/* MILESTONES */}
        <Section title="Milestones & reminders">
          {(rec.milestones || []).length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Add a breeding or ovulation date and save — the standard schedule (ultrasounds, EHV 5/7/9, pre-foaling) will be generated automatically.</p>
          ) : (
            rec.milestones.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}>
                <input type="checkbox" checked={m.is_done} onChange={() => toggleMilestone(m)} style={{ accentColor: 'var(--cr-primary)', width: 18, height: 18 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, textDecoration: m.is_done ? 'line-through' : 'none', color: m.is_done ? 'var(--text-muted)' : 'var(--text-primary)' }}>{m.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{m.due_date}</div>
                </div>
                <button onClick={() => deleteMilestone(m.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
            ))
          )}
        </Section>

        {/* OBSERVATIONS */}
        <Section title="Observation log">
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <input className="form-input" type="date" value={obsDate} onChange={e => setObsDate(e.target.value)} style={{ flex: '0 0 140px' }} />
            <input className="form-input" value={obsNote} onChange={e => setObsNote(e.target.value)} placeholder="e.g. Pawing and rolling" />
            <button className="btn btn-primary btn-sm" onClick={addObservation}>Add</button>
          </div>
          {(rec.observations || []).map(o => (
            <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13 }}>{o.note}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{o.observed_date}</div>
              </div>
              <button onClick={() => deleteObservation(o.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
};

export default BreedingRecordPage;
